import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './SafeIcon';
import { pokemonData } from '../../data/pokemonData';

const { FiSearch } = FiIcons;

const SearchWithSuggestions = ({ 
  className = "", 
  onSearchSubmit, 
  idPrefix = "search",
  isHero = false
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  useEffect(() => {
    // Close suggestions when clicking outside
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper function to get suggestions based on input
  const getSuggestions = (value) => {
    if (!value || value.trim().length <= 2) return [];
    
    const searchTerm = value.toLowerCase().trim();
    return pokemonData.filter(pokemon => 
      pokemon.displayName.toLowerCase().includes(searchTerm) ||
      pokemon.name.includes(searchTerm) ||
      pokemon.id.toString() === searchTerm
    ).slice(0, 5);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Explicitly use pokemonData for filtering suggestions
    if (value.trim().length > 2) {
      const results = getSuggestions(value);
      setSuggestions(results);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Robust onFocus handler to ensure suggestions appear when returning to the input
  const handleFocus = () => {
    if (query.trim().length > 2) {
      // If we have a query but no suggestions (e.g. data updated), refresh them
      if (suggestions.length === 0) {
        setSuggestions(getSuggestions(query));
      }
      setShowSuggestions(true);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setQuery('');
      setShowSuggestions(false);
      if (onSearchSubmit) onSearchSubmit();
    }
  };

  const handleSuggestionClick = (pokemon) => {
    navigate(`/pokemon/${pokemon.name}`);
    setQuery('');
    setShowSuggestions(false);
    if (onSearchSubmit) onSearchSubmit();
  };

  // Styling based on isHero prop
  const inputClasses = isHero
    ? "w-full px-6 py-4 pl-14 pr-16 text-lg text-gray-900 rounded-full border-0 focus:ring-4 focus:ring-white/30 shadow-lg placeholder-gray-500"
    : "w-full px-4 py-2 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  const iconClasses = isHero
    ? "absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6"
    : "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4";

  return (
    <form 
      onSubmit={handleSearch} 
      className={`relative ${className}`} 
      role="search" 
      ref={wrapperRef}
    >
      <div className="relative w-full">
        <input
          type="search"
          id={`${idPrefix}-input`}
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={isHero ? "Search any Pokemon..." : "Search Pokemon..."}
          className={inputClasses}
          aria-label="Search Pokemon"
          autoComplete="off"
        />
        <SafeIcon 
          icon={FiSearch} 
          className={iconClasses}
          aria-hidden="true" 
        />
        
        {/* Hero Submit Button (Inside Input) */}
        {isHero && (
          <button 
            type="submit" 
            className="absolute right-2 top-2 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            aria-label="Search"
          >
            <SafeIcon icon={FiSearch} className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Header Submit Button (Outside Input) */}
      {!isHero && (
        <button
          type="submit"
          className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition-colors hidden md:block"
          aria-label="Search"
        >
          <SafeIcon icon={FiSearch} className="w-4 h-4" />
        </button>
      )}

      {/* Suggestions Dropdown */}
      <AnimatePresence>
        {showSuggestions && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden ${isHero ? 'rounded-2xl' : ''}`}
          >
            <ul>
              {suggestions.map((pokemon) => (
                <li key={pokemon.id}>
                  <button
                    type="button"
                    onClick={() => handleSuggestionClick(pokemon)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-4 transition-colors border-b border-gray-50 last:border-0"
                  >
                    <div className="w-10 h-10 flex-shrink-0 bg-gray-100 rounded-md p-1 overflow-hidden flex items-center justify-center">
                      <img 
                        src={pokemon.image} 
                        alt={pokemon.name} 
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 text-base capitalize">{pokemon.displayName}</div>
                      <div className="text-xs text-gray-500 flex space-x-2 mt-0.5">
                        <span className="font-mono">#{pokemon.id.toString().padStart(3, '0')}</span>
                        <span className="text-gray-300">|</span>
                        <span className="flex space-x-1">
                          {pokemon.types.map(t => (
                            <span key={t} className="uppercase font-semibold tracking-wider text-[10px]">{t}</span>
                          ))}
                        </span>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
};

export default SearchWithSuggestions;